<template>
  <b-overlay
    variant="white"
    :show="showLoading"
    spinner-variant="primary"
    blur="0"
    opacity=".75"
    rounded="sm"
  >
    <!-- Driver Account: Input Fields -->
    <validation-observer ref="createDriverValidation">
      <b-form
        v-if="isNew && !verificationStep && !$route.query.phone"
        @submit.prevent="createDriver"
      >
        <b-row>
          <!-- Field: Phone Number -->
          <b-col
            cols="12"
            md="6"
          >
            <b-row>
              <b-col
                md="3"
              >
                <b-form-group
                  label="Country Code"
                  label-for="country-prefix"
                >
                  <v-select
                    id="country-prefix"
                    v-model="selectedCountry"
                    :clearable="false"
                    :options="allowedCountries"
                  >
                    <template v-slot:option="option">
                      <div class="d-flex justify-content-between align-items-center">
                        <span>{{ option.label }}</span>
                        <country-flag
                          style="z-index:100"
                          :country="option.value.toLowerCase()"
                          size="normal"
                        />
                      </div>
                    </template>
                  </v-select>
                </b-form-group>

              </b-col>
              <b-col
                class="p-0"
                md="9"
              >
                <b-form-group
                  label="Phone Number"
                  label-for="phone-number"
                >
                  <validation-provider
                    #default="{ errors, valid }"
                    name="Phone Number"
                    rules="required|max:15"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="phone-number"
                        v-model="driverData.phoneNumber"
                        type="number"
                        :state="errors[0] || !validPhoneNumber ? false : (valid ? true : null)"
                        :formatter="validatePhoneNumberLength"
                      />
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small
                      v-if="!validPhoneNumber"
                      class="text-danger"
                    >The Phone number is not valid for this country</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="Passcode"
              label-for="passcode"
            >
              <validation-provider
                #default="{ errors }"
                name="Passcode"
                rules="required|max:4|min:4"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="national-id"
                    v-model="driverData.passcode"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- submit button -->
        <b-button
          variant="primary"
          class="mb-1 mb-sm-0 mr-0 mr-sm-1"
          type="submit"
        >
          Create
        </b-button>
      </b-form>
      <validation-observer />
      <p
        v-if="!isNew"
      >Phone Number: {{ driverData.phoneNumber }}
      </p>
      <validation-observer ref="VerificationCodeValidation">
        <b-form
          v-if="isNew && (verificationStep || $route.query.phone)"
          @submit.prevent="verifyPhoneNumber"
        >
          <b-row>
            <!-- Field: Phone Number -->
            <b-col
              cols="12"
              md="12"
            >
              <b-form-group
                label="Verification Code"
                label-for="verification-code"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Verification Code"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verification-code"
                      v-model="verificationCode"
                      autocomplete="off"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- submit button -->
          <b-button
            variant="primary"
            class="mb-1 mb-sm-0 mr-0 mr-sm-1"
            type="submit"
          >
            Verify
          </b-button>
          <b-button
            :disabled="disableReSendVerifyBtn"
            type="reset"
            variant="outline-secondary"
            @click="resendCode"
          >
            Resend Code
          </b-button>
          <p
            v-if="disableReSendVerifyBtn"
            class="mt-1"
          >
            Resend in {{ enableReSendBtnCounter }} seconds
          </p>
        </b-form>
      </validation-observer>
    </validation-observer></b-overlay>
</template>

<script>
import {
  BButton, BRow, BCol, BFormGroup, BForm, BOverlay, BInputGroup, BFormInput,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions } from 'vuex'
import vSelect from 'vue-select'
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
import CountryFlag from 'vue-country-flag'
import { max } from '../../../utils/validations'
import countriesPrefix from './countriesPrefix'
import { isJordanianAdmin } from '@/utils/validate-admin-country'

export default {
  components: {
    CountryFlag,
    vSelect,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BForm,
    BInputGroup,
    BFormInput,
    BOverlay,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    driverData: {
      type: Object,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      disableReSendVerifyBtn: false,
      enableReSendBtnCounter: 30,
      showLoading: false,
      verificationStep: false,
      verificationCode: null,
      selectedCountry: isJordanianAdmin() ? {
        value: 'JO',
        label: '+962',
      } : {
        value: 'LY',
        label: '+218',
      },
      countriesPrefix,
      max,
      phoneUtil: PhoneNumberUtil.getInstance(),
      validPhoneNumber: true,
      formattedPhoneNumber: null,
    }
  },
  computed: {
    enteredPhoneNumber() {
      return this.driverData.phoneNumber
    },
    allowedCountries() {
      const temp = []
      if (isJordanianAdmin()) {
        temp.push({
          value: 'JO',
          label: '+962',
        })
        temp.push({
          value: 'LY',
          label: '+218',
        })
      } else {
        temp.push({
          value: 'LY',
          label: '+218',
        })
      }
      return temp
    },
  },
  watch: {
    enteredPhoneNumber(val) {
      if (!val) this.validPhoneNumber = true
      const phoneNumber = this.phoneUtil.parse(this.driverData.phoneNumber, this.selectedCountry.value)
      if (this.phoneUtil.isValidNumberForRegion(phoneNumber, this.selectedCountry.value)) {
        this.formattedPhoneNumber = this.phoneUtil.format(phoneNumber, PhoneNumberFormat.E164)
        this.validPhoneNumber = true
      } else {
        this.validPhoneNumber = false
      }
    },
  },
  methods: {
    ...mapActions({
      fetchDrivers: 'drivers/fetchDrivers',
      resendConfirmationCode: 'drivers/resendConfirmationCode',
    }),
    validatePhoneNumberLength(e) {
      if (e.length > 15) {
        this.driverData.phoneNumber = e.slice(0, -1)
        return this.driverData.phoneNumber
      }
      return e
    },
    disableResendVerificationButton() {
      this.disableReSendVerifyBtn = true
      this.enableReSendBtnCounter = 30
      const counterInterval = setInterval(() => {
        this.enableReSendBtnCounter -= 1
      }, 1000)
      setTimeout(() => {
        this.disableReSendVerifyBtn = false
        clearInterval(counterInterval)
      }, 30000)
    },
    async createDriver() {
      const valid = await this.$refs.createDriverValidation.validate()
      if (!valid || !this.validPhoneNumber) {
        return
      }
      console.log(this.driverData.passcode)
      const result = await this.$store.dispatch(
        'drivers/createDriver',
        {
          phoneNumber: this.formattedPhoneNumber,
          passcode: this.driverData.passcode,
        },
      )
      if (result.created) {
        this.verificationStep = true
        this.disableResendVerificationButton()
      } else {
        const drivers = await this.fetchDrivers({ phoneNumber: this.formattedPhoneNumber })
        if (drivers.drivers.length === 0) {
          this.$swal({
            title: 'Error!',
            text: result.error.message,
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        } else {
          this.verificationStep = true
          this.disableResendVerificationButton()
          await this.resendConfirmationCode(this.formattedPhoneNumber)
        }
      }
    },
    getDriverByPhoneNumber() {
      this.getDriverInterval = setInterval(() => {
        this.fetchDrivers({ phoneNumber: this.formattedPhoneNumber }).then(result => {
          const { drivers } = result
          if (!drivers.length) return
          clearInterval(this.getDriverInterval)
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Created Successfully',
              icon: 'CoffeeIcon',
              variant: 'success',
              text: 'Driver created successfully',
            },
          })
          this.$router.push({ name: 'drivers-edit', params: { id: drivers[0].id } }).catch(() => {})
        })
      }, 5000)
    },
    async verifyPhoneNumber() {
      this.showLoading = true
      if (this.$route.query.phone) {
        this.formattedPhoneNumber = this.$route.query.phone
      }
      const result = await this.$store.dispatch(
        'drivers/verifyPhoneNumber',
        {
          username: this.formattedPhoneNumber,
          code: this.verificationCode,
        },
      )
      if (result.verified) {
        this.getDriverByPhoneNumber()
      } else {
        this.showLoading = false
        this.$swal({
          title: 'Error!',
          text: 'Resend the verification code again.',
          icon: 'error',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      }
    },
    async resendCode() {
      await this.resendConfirmationCode(this.formattedPhoneNumber)
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: 'Verification Code Sent',
          icon: 'CoffeeIcon',
          variant: 'success',
          text: `A message has been sent to ${this.formattedPhoneNumber}`,
        },
      })
      this.disableResendVerificationButton()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
